import { Box, Grid, ListSubheader, MenuItem, Paper, TextField, Typography, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CommissionTypes, DateRangeReportFilter, IdReportFilter, LotStatuses, RecencyReportFilter, Report, ReportFilterTargetFields, ReportMailScheduleTypes, ReportTemplateTypes, StageSalesStatuses } from "../../api/dtos";
import { StyledButton } from "../../components/StyledButton";
import TitleBox from "../../components/TitleBox";
import usePostReportSave from "../../hooks/data/usePostReportSave";
import useReportById from "../../hooks/data/useReportById";
import useReportTemplates from "../../hooks/data/useReportTemplates";
import ProjectMultiSelect from "./ProjectMultiSelect";
import RecencyReportFilterSelect from "./RecencyReportFilterSelect";
import StageMultiSelect from "./StageMultiSelect";
import SalesTypeFilterSelect from "./SalesTypeFilterSelect";
import ArchivedStagesSelect from "./ArchivedStagesSelect";
import SetLinks from "../../components/layout/SetLinks";
import LotStatusFilterSelect from "./LotStatusFilterSelect";
import { DateRangeReportFilterSelect } from "./DateRangeReportFilterSelect";

interface IReportDetails {
    edit?: boolean;
}

const ReportDetails: FC<IReportDetails> = ({
    edit
}) => {
    const {data: templates} = useReportTemplates();

    const params = useParams();
    const theme = useTheme();

    const snackbar = useSnackbar();
    const navigate = useNavigate();

    const reportId = Number(params.reportId);

    const {data: report} = useReportById(reportId);

    const [name, setName] = useState<string>();

    const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
    const [selectedStageIds, setSelectedStageIds] = useState<number[]>([]);
    const [selectedCommissionType, setSelectedCommissionType] = useState<CommissionTypes>();
    const [selectedLotStatus, setSelectedLotStatus] = useState<LotStatuses>();
    const [includeArchivedStages, setIncludeArchivedStages] = useState<boolean>();

    const [recencyReportFilters, setRecencyReportFilters] = useState<RecencyReportFilter[]>();
    const [dateRangeFilters, setDateRangeFilters] = useState<DateRangeReportFilter[]>();

    const [selectedTemplateId, setSelectedTemplateId] = useState<number>(-1);
    const [selectedTemplateType, setSelectedTemplateType] = useState<ReportTemplateTypes>();
    const [selectedSchedule, setSelectedSchedule] = useState<ReportMailScheduleTypes>(ReportMailScheduleTypes.Never);
    const [mailingList, setMailingList] = useState<string>();
    const [ccList, setCcList] = useState<string>();

    useEffect(() => {
        setName(_name => report?.report?.name ?? "");
        setSelectedSchedule(_schedule => report?.report?.scheduleType ?? ReportMailScheduleTypes.Never);
        setMailingList(_mailingList => report?.report?.mailingList ?? "");
        setCcList(_ccList => report?.report?.ccList ?? "");
    }, [report?.report])

    useEffect(() => {
        setSelectedProjectIds(_projectIds => 
            report?.idFilters?.filter(filter => 
                filter.filterTargetField == ReportFilterTargetFields.ProjectId
            ).map(filter => filter.filterId ?? -1) ?? []);
        setSelectedStageIds(_stageIds => 
            report?.idFilters?.filter(filter => 
                filter.filterTargetField == ReportFilterTargetFields.StageId
            ).map(filter => filter.filterId ?? -1) ?? []);
        setSelectedCommissionType(_old => 
            (report?.idFilters?.filter(filter => 
                filter.filterTargetField == ReportFilterTargetFields.CommissionTypeExchange
            )[0] as IdReportFilter)?.filterId
        )
        setIncludeArchivedStages(_old => 
            (report?.idFilters?.filter(filter => 
                filter.filterTargetField == ReportFilterTargetFields.StageSalesStatus &&
                (filter as IdReportFilter).filterId == StageSalesStatuses.Archived 
            )?.length ?? -1) > 0
        )
        setRecencyReportFilters(_filters => 
            [...(report?.recencyFilters ?? [])]
        )
        setDateRangeFilters(_filters => 
            [...(report?.dateRangeFilters ?? [])]
        )
    }, [report])

    useEffect(() => {
        setSelectedTemplateId(templates?.filter(template => template.id == report?.report?.templateId)[0]?.id ?? -1);
    }, [templates, report?.report?.templateId])

    useEffect(() => {
        setSelectedTemplateType(templates?.filter(template => template.id == selectedTemplateId)[0]?.templateType);
        if(templates?.filter(template => template.id == selectedTemplateId)[0]?.templateType != ReportTemplateTypes.Custom) {
            setSelectedStageIds([]);
        }
    }, [selectedTemplateId]);

    useEffect(() => {
        if(selectedTemplateType != ReportTemplateTypes.GrossRealisationReport) {
            setSelectedCommissionType(undefined);
        }

        if(selectedTemplateType != ReportTemplateTypes.StageLotCounts) {
            setIncludeArchivedStages(undefined);
        }
    }, [selectedTemplateType])

    const save = usePostReportSave();

    const handleSave = async () => {
        const projectFilters = selectedProjectIds.map(projectId => new IdReportFilter({filterTargetField: ReportFilterTargetFields.ProjectId, filterId: projectId}))
        const stageFilters = selectedStageIds.map(stageId => new IdReportFilter({filterTargetField: ReportFilterTargetFields.StageId, filterId: stageId}))
        const salesTypeFilters = [];
        if(selectedCommissionType) {
            salesTypeFilters.push(new IdReportFilter({
                filterTargetField: ReportFilterTargetFields.CommissionTypeExchange,
                filterId: selectedCommissionType
            }));
        }
        const includeArchivedStagesFilters = [];
        if(includeArchivedStages) {
            includeArchivedStagesFilters.push(new IdReportFilter({
                filterTargetField: ReportFilterTargetFields.StageSalesStatus,
                filterId: StageSalesStatuses.Archived
            }))
        }
        const lotStatusFilters = [];
        if(selectedLotStatus) {
            lotStatusFilters.push(new IdReportFilter({
                filterTargetField: ReportFilterTargetFields.LotStatus,
                filterId: selectedLotStatus
            }))
        }

        const report = new Report({
            id: edit ? reportId : undefined,
            name: name,
            templateId: selectedTemplateId,
            mailingList: mailingList,
            ccList: ccList,
            scheduleType: selectedSchedule,
        })
        
        const idFilters = [...projectFilters, ...stageFilters, ...salesTypeFilters, ...lotStatusFilters, ...includeArchivedStagesFilters];

        save(report, idFilters, dateRangeFilters, recencyReportFilters)
            .then(() => {
                snackbar.enqueueSnackbar({variant: "success", message: "Successfully Saved Report"});
                navigate("/reports")
            })
            .catch(() => snackbar.enqueueSnackbar({variant: "error", message: "Failed To Save Report"}));
    }

    const emailOnlyTemplateTypes = useMemo(() => [
        ReportTemplateTypes.EmailOnlyLotSummary,
        ReportTemplateTypes.EmailOnlySalesSummary
    ], []);

    const emailOnlyTemplateOptions = useMemo(() => (
        templates?.filter(template => 
            template.templateType && 
            emailOnlyTemplateTypes.includes(template.templateType)
        ).map(template => (
            <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
        ))
    ), [templates, emailOnlyTemplateTypes]);

    const predefinedTemplateOptions = useMemo(() => (
        templates?.filter(template => 
            template.templateType && 
            template.templateType != ReportTemplateTypes.Custom && 
            !emailOnlyTemplateTypes.includes(template.templateType)
        ).map(template => (
            <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
        ))
    ), [templates, emailOnlyTemplateTypes]);
    
    const customTemplateOptions = useMemo(() => (
        templates?.filter(template => 
            template.templateType == ReportTemplateTypes.Custom
        ).map(template => (
            <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
        ))
    ), [templates]);

    const displayMailFields = useMemo(() => (
        selectedSchedule != ReportMailScheduleTypes.Never ||
        (
            selectedTemplateType && 
            emailOnlyTemplateTypes.includes(selectedTemplateType)
        )
    ), [selectedTemplateType, emailOnlyTemplateTypes, selectedSchedule]);
    
    return (
        <>
            <SetLinks
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <TitleBox>
                <Typography 
                    variant="h2"
                >
                    {edit ? "Edit" : "New"} Report
                </Typography>
            </TitleBox>
            <Paper
                sx={{
                    padding: theme.spacing(2)
                }}
            >
                <Box
                    sx={{
                        width: 500,
                        marginBottom: theme.spacing(3)
                    }}
                >
                    <TextField
                        fullWidth
                        label="Name"
                        onChange={event => setName(event.target.value)}
                        value={name ?? ""}
                        sx={{
                            marginBottom: 2
                        }}
                    />
                    <TextField
                        fullWidth
                        select
                        label="Template"
                        onChange={event => setSelectedTemplateId(templates?.filter(x => x.id == Number(event.target.value))[0]?.id ?? -1)}
                        value={selectedTemplateId == -1 ? "" : selectedTemplateId}
                    >
                        <ListSubheader>Email Only Templates</ListSubheader>
                        {emailOnlyTemplateOptions}
                        <ListSubheader>Predefined Templates</ListSubheader>
                        {predefinedTemplateOptions}
                        <ListSubheader>Custom Templates</ListSubheader>
                        {customTemplateOptions}
                    </TextField>
                </Box>
                {
                    selectedTemplateType && 
                    selectedTemplateType != ReportTemplateTypes.SalesAdviceToContract &&
                    selectedTemplateType != ReportTemplateTypes.EmailOnlyLotSummary &&
                    <>
                        <Typography
                            sx={{
                                marginBottom: 2
                            }}
                        >
                            Filters:
                        </Typography>
                        <Grid 
                            sx={{
                                marginBottom: theme.spacing(3)
                            }}
                            spacing={2}
                            container
                        >    
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.Custom ||
                                    selectedTemplateType == ReportTemplateTypes.SalesSummary ||
                                    selectedTemplateType == ReportTemplateTypes.LotSummary ||
                                    selectedTemplateType == ReportTemplateTypes.ContractsOverdue ||
                                    selectedTemplateType == ReportTemplateTypes.CommissionReport ||
                                    selectedTemplateType == ReportTemplateTypes.GrossRealisationReport ||
                                    selectedTemplateType == ReportTemplateTypes.SalesRateReport ||
                                    selectedTemplateType == ReportTemplateTypes.UpcomingSettlements ||
                                    selectedTemplateType == ReportTemplateTypes.StageLotCounts ||
                                    selectedTemplateType == ReportTemplateTypes.LotPricingComparison
                                ) && 
                                <ProjectMultiSelect 
                                    selectedProjectIds={selectedProjectIds} 
                                    onChange={(projectIds) => setSelectedProjectIds(projectIds)} 
                                />
                            }
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.Custom ||
                                    selectedTemplateType == ReportTemplateTypes.LotPricingComparison
                                ) &&
                                <StageMultiSelect 
                                    selectedProjectIds={selectedProjectIds} 
                                    selectedStageIds={selectedStageIds} 
                                    onChange={(val) => setSelectedStageIds(val)}
                                />
                            }
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.LotSummary ||
                                    selectedTemplateType == ReportTemplateTypes.CommissionReport
                                ) &&
                                <RecencyReportFilterSelect
                                    report={report?.report}
                                    requestRecencyFilters={report?.recencyFilters}
                                    setRecencyReportFilters={setRecencyReportFilters}
                                    applicableFields={[
                                        ReportFilterTargetFields.ContractExchangeDate, 
                                        ReportFilterTargetFields.ContractSettleDate
                                    ]}
                                />
                            }
                            {
                                (
                                    // selectedTemplateType == ReportTemplateTypes.EmailOnlyLotSummary ||
                                    selectedTemplateType == ReportTemplateTypes.EmailOnlySalesSummary
                                ) &&
                                <DateRangeReportFilterSelect
                                    dateRangeReportFilters={dateRangeFilters}
                                    setDateRangeReportFilters={setDateRangeFilters}
                                    applicableFields={[
                                        ReportFilterTargetFields.EmailOnlyParams
                                    ]}
                                />
                            }
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.GrossRealisationReport
                                ) &&
                                <SalesTypeFilterSelect 
                                    selectedCommissionType={selectedCommissionType}
                                    setSelectedCommissionType={setSelectedCommissionType}
                                />
                            }
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.StageLotCounts
                                ) &&
                                <ArchivedStagesSelect 
                                    setIncludeArchivedStages={setIncludeArchivedStages}
                                    includeArchivedStages={includeArchivedStages}
                                />
                            }
                            {
                                (
                                    selectedTemplateType == ReportTemplateTypes.Custom
                                ) &&
                                <LotStatusFilterSelect
                                    selectedLotStatus={selectedLotStatus}
                                    setSelectedLotStatus={setSelectedLotStatus}
                                />
                            }
                        </Grid>  
                    </>
                }
                {
                    selectedTemplateId &&
                    <Grid 
                        gap={2}
                        container
                    >
                        <Grid 
                            size={{
                                xs: 12
                            }}
                        >
                            <Typography>Mailing:</Typography>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12
                            }}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between"
                            }}
                        >
                            <TextField
                                select
                                sx={{
                                    width: 200
                                }}
                                label="Schedule"
                                onChange={event => setSelectedSchedule(Number(event.target.value))}
                                value={selectedSchedule}
                            >
                                <MenuItem key={ReportMailScheduleTypes.Never} value={ReportMailScheduleTypes.Never}>Never</MenuItem>
                                <MenuItem key={ReportMailScheduleTypes.Daily} value={ReportMailScheduleTypes.Daily}>Daily</MenuItem>
                                <MenuItem key={ReportMailScheduleTypes.Weekly} value={ReportMailScheduleTypes.Weekly}>Weekly</MenuItem>
                                <MenuItem key={ReportMailScheduleTypes.Monthly} value={ReportMailScheduleTypes.Monthly}>Monthly</MenuItem>
                                <MenuItem key={ReportMailScheduleTypes.Yearly} value={ReportMailScheduleTypes.Yearly}>Yearly</MenuItem>
                            </TextField>
                            {/* <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <StyledButton
                                    sx={{
                                        flexShrink: 1
                                    }}
                                >
                                    Trigger Email
                                </StyledButton>
                            </Box> */}
                        </Grid>
                        {
                            displayMailFields && 
                            <Grid
                                size={{
                                    xs: 12
                                }}
                                gap={2}
                                container
                            >
                                <TextField
                                    label="Mailing List (CSV)"
                                    fullWidth
                                    onChange={event => setMailingList(event.target.value)}
                                    value={mailingList ?? ""}
                                />
                                <TextField
                                    label="CC List (CSV)"
                                    fullWidth
                                    onChange={event => setCcList(event.target.value)}
                                    value={ccList ?? ""}
                                />
                            </Grid>
                        }
                    </Grid>
                }
                {
                    selectedTemplateId &&
                    <Box
                        sx={{
                            marginTop: theme.spacing(3)
                        }}
                    >
                        <StyledButton
                            onClick={handleSave}
                        >
                            Save Report
                        </StyledButton>
                    </Box>
                }
            </Paper>
        </>
    );
}

export default ReportDetails;